import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Download, Upload, Store as StoreIcon, ArrowRight, Loader2, Sparkles,
  HelpCircle, Network, GitBranch, Rss, Braces, Tags, Boxes, Gauge, Check, FileSpreadsheet,
} from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/i18n";
import { useStore } from "@/context/StoreProvider";
import { formatApiError } from "@/context/AuthProvider";

export default function ImportCatalog() {
  const t = useT();
  const navigate = useNavigate();
  const { store, loading } = useStore();
  const [busy, setBusy] = useState(null); // 'shopify' | 'csv' | null
  const [report, setReport] = useState(null);
  const [shopDomain, setShopDomain] = useState("");
  const fileRef = useRef(null);

  if (loading) return <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-t-brand" />;
  if (!store) {
    return (
      <div className="mx-auto max-w-lg py-16 text-center">
        <StoreIcon className="mx-auto h-10 w-10 text-muted-foreground" />
        <p className="mt-4 text-muted-foreground">{t("dashboard.home.noStore")}</p>
        <Link to="/dashboard/store" className="mt-4 inline-flex rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground">{t("dashboard.home.noStoreCta")}</Link>
      </div>
    );
  }

  const importShopify = async () => {
    setBusy("shopify");
    try {
      const form = new FormData();
      form.append("store_id", store.id);
      form.append("shop_domain", shopDomain);
      const { data } = await api.post("/imports/shopify", form);
      setReport(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };

  const importCsv = async (file) => {
    setBusy("csv");
    try {
      const form = new FormData();
      form.append("store_id", store.id);
      form.append("file", file);
      const { data } = await api.post("/imports/csv", form, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (busy) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center text-center">
        <div className="relative">
          <div className="h-16 w-16 animate-spin rounded-full border-2 border-border border-t-brand" />
          <Sparkles className="absolute inset-0 m-auto h-6 w-6 text-brand" />
        </div>
        <p className="mt-6 font-display text-lg font-bold">{t("import.importing")}</p>
      </div>
    );
  }

  if (report) return <Report report={report} store={store} onReset={() => setReport(null)} navigate={navigate} />;

  return (
    <div className="max-w-3xl">
      <h1 className="flex items-center gap-2 font-display text-3xl font-extrabold tracking-tighter">
        <Download className="h-6 w-6 text-brand" />{t("import.title")}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">{t("import.subtitle")}</p>

      <div className="mt-8 grid gap-4 md:grid-cols-2">
        {/* Shopify */}
        <div className="flex flex-col rounded-lg border border-border bg-card p-6" data-testid="import-shopify-card">
          <div className="flex items-center gap-2">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[#95BF47]/15 text-[#5E8E3E]"><StoreIcon className="h-5 w-5" /></span>
            <h2 className="font-display text-lg font-bold">{t("import.shopify")}</h2>
          </div>
          <p className="mt-3 flex-1 text-sm text-muted-foreground">{t("import.shopifyDesc")}</p>
          <input value={shopDomain} onChange={(e) => setShopDomain(e.target.value)} data-testid="shopify-domain"
            placeholder={t("import.shopDomain")}
            className="mt-4 h-10 w-full rounded-md border border-input bg-background px-3 text-sm outline-none focus:ring-2 focus:ring-brand" />
          <button onClick={importShopify} data-testid="import-shopify-btn"
            className="mt-3 inline-flex items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-transform hover:opacity-90 active:scale-95">
            {t("import.shopifyBtn")}<ArrowRight className="h-4 w-4" />
          </button>
          <p className="mt-2 text-[11px] text-muted-foreground">{t("import.shopifyDemo")}</p>
        </div>

        {/* CSV */}
        <div className="flex flex-col rounded-lg border border-border bg-card p-6" data-testid="import-csv-card">
          <div className="flex items-center gap-2">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-brand/10 text-brand"><FileSpreadsheet className="h-5 w-5" /></span>
            <h2 className="font-display text-lg font-bold">{t("import.csv")}</h2>
          </div>
          <p className="mt-3 flex-1 text-sm text-muted-foreground">{t("import.csvDesc")}</p>
          <button onClick={() => fileRef.current?.click()} data-testid="import-csv-btn"
            className="mt-4 inline-flex items-center justify-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm font-medium transition-colors hover:bg-secondary">
            <Upload className="h-4 w-4" />{t("import.csvBtn")}
          </button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden"
            onChange={(e) => e.target.files?.[0] && importCsv(e.target.files[0])} />
        </div>
      </div>
    </div>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-md border border-border bg-card p-3">
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand/10 text-brand"><Icon className="h-4 w-4" /></span>
      <div className="min-w-0">
        <p className="font-mono text-lg font-bold leading-none text-success">+{value}</p>
        <p className="mt-0.5 truncate text-xs text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

function Report({ report, store, onReset, navigate }) {
  const t = useT();
  const a = report.after;
  const metrics = [
    { icon: HelpCircle, label: t("import.faqGenerated"), value: a.faq_generated },
    { icon: Network, label: t("import.kgNodes"), value: a.knowledge_graph_nodes },
    { icon: GitBranch, label: t("import.semanticRelations"), value: a.semantic_relations },
    { icon: Rss, label: t("import.aiFeeds"), value: a.ai_feeds },
    { icon: Braces, label: t("import.jsonldItems"), value: a.jsonld_items },
    { icon: Tags, label: t("import.semanticKeywords"), value: a.semantic_keywords },
    { icon: Boxes, label: t("import.productEntities"), value: a.product_entities },
    { icon: Check, label: t("import.structuredData"), value: 1 },
  ];

  return (
    <div className="max-w-4xl">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="flex items-center gap-2 text-brand"><Sparkles className="h-5 w-5" /><span className="text-xs font-bold uppercase tracking-widest">{t("import.imported", { count: report.imported })}</span></div>
        <h1 className="mt-2 font-display text-3xl font-extrabold tracking-tighter">{t("import.reportTitle")}</h1>
        <p className="mt-1 text-sm text-muted-foreground">{t("import.reportSubtitle")}</p>
      </motion.div>

      {/* Before vs After */}
      <div className="mt-8 grid items-stretch gap-4 md:grid-cols-[1fr_auto_1fr]">
        <div className="rounded-lg border border-border bg-secondary/30 p-6" data-testid="report-before">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">{t("import.before")}</p>
          <p className="mt-3 font-display text-2xl font-bold">{report.before.product_count}</p>
          <p className="text-sm text-muted-foreground">{t("import.beforeLabel")}</p>
          <p className="mt-2 text-xs text-muted-foreground">{report.before.product_count} {t("import.beforeProducts")}</p>
        </div>
        <div className="hidden items-center justify-center md:flex"><ArrowRight className="h-6 w-6 text-brand" /></div>
        <div className="relative overflow-hidden rounded-lg border border-brand/40 bg-primary p-6 text-primary-foreground" data-testid="report-after">
          <div className="dotgrid absolute inset-0 opacity-[0.08]" />
          <div className="relative z-10">
            <p className="text-xs font-bold uppercase tracking-widest text-primary-foreground/60">{t("import.after")}</p>
            <div className="mt-3 flex items-center gap-3">
              <span className="font-display text-2xl font-bold">{a.product_count}</span>
              <span className="rounded-full bg-brand px-2 py-0.5 text-xs font-medium text-brand-foreground">{t("import.aiReadiness")}: {a.ai_readiness_score}</span>
            </div>
            <p className="text-sm text-primary-foreground/70">{t("import.afterLabel")}</p>
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="report-metrics">
        {metrics.map((m, i) => <Metric key={i} {...m} />)}
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <button onClick={() => navigate("/dashboard/identity")} data-testid="report-view-identity"
          className="inline-flex items-center gap-2 rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-transform hover:opacity-90 active:scale-95">
          <Gauge className="h-4 w-4" />{t("import.viewIdentity")}
        </button>
        <a href={`/store/${store.id}`} target="_blank" rel="noreferrer" data-testid="report-view-store"
          className="inline-flex items-center gap-2 rounded-md border border-border px-5 py-2.5 text-sm font-medium transition-colors hover:bg-secondary">
          <StoreIcon className="h-4 w-4" />{t("import.viewStore")}
        </a>
        <button onClick={onReset} data-testid="report-import-another"
          className="inline-flex items-center gap-2 rounded-md border border-border px-5 py-2.5 text-sm font-medium transition-colors hover:bg-secondary">
          <Download className="h-4 w-4" />{t("import.importAnother")}
        </button>
      </div>
    </div>
  );
}
